import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import LocaleLink from '../LocaleLink';

// Export optimized image components
import LazyBackgroundImage from './LazyBackgroundImage';
export { default as LazyImage } from './LazyImage';
export { default as LazyBackgroundImage } from './LazyBackgroundImage';

export const CtaImageSection = styled(LazyBackgroundImage)`
  margin-top: 40px;

  @media (max-width: 768px) {
    margin-top: 30px;
  }
`;

// ============================================================================
// SHARED BACKGROUND GRADIENT
// ============================================================================
export const GRADIENT_BACKGROUND = `
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(250, 252, 255, 0.95) 20%,
    rgba(245, 249, 255, 0.9) 40%,
    rgba(240, 245, 252, 0.85) 60%,
    rgba(236, 242, 249, 0.9) 80%,
    rgba(248, 250, 252, 0.95) 100%
  );
`;

// ============================================================================
// SHARED CONTAINER COMPONENTS
// ============================================================================
export const PageContainer = styled.div`
  min-height: 100vh;
  ${GRADIENT_BACKGROUND}
  padding: 50px 0 100px;
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

export const Section = styled.section`
  padding: 20px 0 40px;
  background: transparent;
  
  &.large-padding {
    padding: 120px 0;
    
    &:first-child {
      padding-top: 60px;
    }
  }
`;

// ============================================================================
// SHARED TYPOGRAPHY COMPONENTS
// ============================================================================
export const PageTitle = styled(motion.h1)`
  font-size: clamp(35px, 10vw, 40px);
  font-weight: 900;
  color: #1d334e;
  text-align: center;
  margin-bottom: 20px;
  letter-spacing: -0.04em;
  line-height: 0.9;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  
  &.with-underline {
    margin-bottom: 50px;
  }
  
  &.with-underline::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 5px;
    background: linear-gradient(135deg, #1d334e, #BF1922);
    border-radius: 2px;
  }
`;

export const PageSubtitle = styled(motion.p)`
  font-size: clamp(18px, 3vw, 24px);
  color: rgba(29, 51, 78, 0.8);
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.6;
  font-weight: 400;
`;

export const SectionTitle = styled(motion.h2)`
  font-size: clamp(26px, 6vw, 32px);
  font-weight: 900;
  color: #1d334e;
  text-align: center;
  margin-bottom: 40px;
  letter-spacing: -0.04em;
  line-height: 1.1;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  
  &.with-underline::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #1d334e, #BF1922);
    border-radius: 2px;
  }
`;

/** Brand dot bullets — shared by BulletList and prose `ul` inside ContentText (see data-list-variant). */
const brandBulletListStyles = css`
  margin: 0 0 32px 0;
  padding-left: 20px;
  list-style: none;

  li {
    font-size: clamp(16px, 2.5vw, 16px);
    color: rgba(29, 51, 78, 0.8);
    margin-bottom: 10px;
    position: relative;
    padding-left: 4px;

    &::before {
      content: '';
      position: absolute;
      left: -16px;
      top: 0.55em;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #bf1922;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #1d334e;
    font-weight: 600;
  }
`;

export const ContentText = styled(motion.div)`
  text-align: left;
  margin-top: 16px;
  margin-bottom: 24px;
  &.centered {
    text-align: center;
  }
  
  &.left-aligned {
    margin-left: 0;
    margin-right: 0;
  }
  
  p {
    font-size: clamp(16px, 2.5vw, 16px);
    color: rgba(29, 51, 78, 0.8);
    font-weight: 400;
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    color: #1d334e;
    font-weight: 600;
  }

  /* Raw HTML lists (and any ul without data-list-variant) get the same brand dots as BulletList */
  ul:not([data-list-variant]) {
    ${brandBulletListStyles};
  }
`;

export const LeftAlignedContentText = styled(ContentText).attrs({ className: 'left-aligned' })``;

export const BulletList = styled.ul.attrs({ 'data-list-variant': 'brand-dots' })`
  ${brandBulletListStyles};
`;

// ============================================================================
// SHARED GLASSMORPHISM COMPONENTS
// ============================================================================
export const GlassCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  }
  
  &.large-padding {
    padding: 48px;
    
    @media (max-width: 768px) {
      padding: 32px 24px;
    }
  }

  &.medium-padding {
    padding: 32px;
    
    @media (max-width: 768px) {
      padding: 24px 16px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

// ============================================================================
// CARD-STYLE BULLET LIST (text-only)
// ============================================================================
export const TextBulletCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const TextBulletCard = styled(GlassCard)`
  padding: 22px 24px;
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 18px 20px;
  }
`;

export const TextBulletCardText = styled.p`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin: 0;
  
  strong {
    color: #1d334e;
    font-weight: 600;
  }
`;

export const ImageContainer = styled(motion.div)`
  position: relative;
  height: 400px;
  border-radius: 24px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-color: #ffffff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

export const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 40px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const ImageTitle = styled.h3`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

export const ImageDescription = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// ============================================================================
// SHARED ANIMATION VARIANTS & KEYFRAMES
// ============================================================================
export const fadeInUpKeyframe = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

export const fadeInLeft = {
  initial: { opacity: 0, x: -30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

export const fadeInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

/** Hero / above-the-fold smooth reveal: use with motion container + motion children on any page */
export const heroStaggerContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

export const heroStaggerItemVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
  },
};

// ============================================================================
// SHARED GRID COMPONENTS
// ============================================================================
export const GridContainer = styled.div`
  display: grid;
  gap: 32px;
  margin: 40px 0;
  width: 100%;
  
  &.two-columns {
    grid-template-columns: repeat(2, 1fr);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }
  
  &.three-columns {
    grid-template-columns: repeat(3, 1fr);
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }
  
  &.four-columns {
    grid-template-columns: repeat(2, 1fr);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }
  
  &.single-column {
    grid-template-columns: 1fr;
    gap: 60px;
    max-width: 1000px;
  }
`;

// ============================================================================
// STRENGTH CARD / NUMBERED POINT CARD (Core Strengths, Operating Architecture)
// ============================================================================
export const StrengthCard = styled.div`
  text-align: ${props => props.$align || 'left'};
  padding: 40px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 24px;
  position: relative;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => (props.$isVisible !== false ? 1 : 0)};
  transform: translateY(${props => (props.$isVisible !== false ? '0' : '30px')}) scale(${props => (props.$isVisible !== false ? '1' : '0.95')});
  transition-delay: ${props => props.$delay || '0s'};
  
  &::before {
    content: '${props => props.$number || ''}';
    position: absolute;
    top: -20px;
    ${props => props.$align === 'right' ? 'right: 40px;' : 'left: 40px;'}
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #1d334e, #BF1922);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 18px;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);
    transition: box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
  }
  
  &:hover {
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
    
    &::before {
      box-shadow: 0 10px 22px rgba(0, 0, 0, 0.2);
    }
  }
`;

export const StrengthCardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 16px;
  line-height: 1.3;
`;

export const StrengthCardDescription = styled.p`
  font-size: 15px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin: 0;
`;

export const StrengthCardsGrid = styled(GridContainer).attrs({ className: 'two-columns' })`
  margin-top: 32px;
  margin-bottom: 32px;
`;

// ============================================================================
// BUSINESS ICON COMPONENT (CONSOLIDATED)
// ============================================================================
export const BusinessIcon = ({ type, size = 24 }) => {
  const icons = {
    solutions: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    partnership: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A2.99 2.99 0 0 0 17.07 7c-.8 0-1.54.37-2.01.95l-2.28 2.8c-.44.53-.66 1.2-.66 1.88v2.37h-2v-5c0-1.1.9-2 2-2h1c.34 0 .68.07 1 .2l2.5 1.25V6.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V9l-1.5-.75L15 10.5v11.5h5z" />
      </svg>
    ),
    quality: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
    ),
    excellence: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    expertise: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L23 9l-11-6zM6.5 12.5v4l5.5 3 5.5-3v-4L12 15.5l-5.5-3z" />
      </svg>
    ),
    communication: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
      </svg>
    ),
    innovation: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z" />
      </svg>
    ),
    consulting: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
      </svg>
    ),
    construction: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    industrial: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
    ),
    electrical: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z" />
      </svg>
    ),
    chemical: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L23 9l-11-6zM6.5 12.5v4l5.5 3 5.5-3v-4L12 15.5l-5.5-3z" />
      </svg>
    ),
    procurement: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    sourcing: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A2.99 2.99 0 0 0 17.07 7c-.8 0-1.54.37-2.01.95l-2.28 2.8c-.44.53-.66 1.2-.66 1.88v2.37h-2v-5c0-1.1.9-2 2-2h1c.34 0 .68.07 1 .2l2.5 1.25V6.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V9l-1.5-.75L15 10.5v11.5h5z" />
      </svg>
    ),
    logistics: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
      </svg>
    ),
    marketing: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L23 9l-11-6zM6.5 12.5v4l5.5 3 5.5-3v-4L12 15.5l-5.5-3z" />
      </svg>
    ),
    optimization: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
      </svg>
    ),
    pcb: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M22 9V7h-2V5c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v2H0v2h2v2H0v2h2v2c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-2h2v-2h-2V9h2zM9 12c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm6 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" />
      </svg>
    ),
    petroleum: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
      </svg>
    ),
    vessel: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l1.89-6.68c.08-.26.06-.54-.06-.78s-.32-.42-.58-.5L20 10.62V6c0-1.1-.9-2-2-2h-3V1H9v3H6c-1.1 0-2 .9-2 2v4.62l-1.29.42c-.26.08-.46.26-.58.5s-.14.52-.06.78L3.95 19z" />
      </svg>
    ),
    realestate: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
      </svg>
    ),
    investment: (
      <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
      </svg>
    )
  };

  return icons[type] || icons.solutions;
};

// ============================================================================
// FORM COMPONENTS
// ============================================================================
export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1d334e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FormInput = styled.input`
  padding: 16px 20px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  background: rgba(244, 244, 244, 0.85);
  backdrop-filter: blur(10px);
  font-size: 16px;
  color: #1d334e;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &::placeholder {
    color: rgba(29, 51, 78, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(191, 25, 34, 0.8);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 0 4px rgba(191, 25, 34, 0.1);
  }
`;

export const FormSelect = styled.select`
  padding: 16px 40px 16px 20px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  background: rgba(244, 244, 244, 0.85);
  backdrop-filter: blur(10px);
  font-size: 16px;
  color: #1d334e;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  appearance: none;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;
  
  &:focus {
    outline: none;
    border-color: rgba(191, 25, 34, 0.8);
    background-color: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 0 4px rgba(191, 25, 34, 0.1);
  }
  
  option {
    background: white;
    color: #1d334e;
    padding: 8px 12px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const FormTextArea = styled.textarea`
  padding: 16px 20px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  background: rgba(244, 244, 244, 0.85);
  backdrop-filter: blur(10px);
  font-size: 16px;
  color: #1d334e;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &::placeholder {
    color: rgba(29, 51, 78, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(191, 25, 34, 0.8);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 0 4px rgba(191, 25, 34, 0.1);
  }
`;

export const FormButton = styled(motion.button)`
  padding: 18px 36px;
  background: linear-gradient(135deg, #BF1922 0%, #8B1419 100%);
  color: #ffffff;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(191, 25, 34, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// ============================================================================
// CARD COMPONENTS
// ============================================================================
export const FeatureCard = styled(GlassCard)`
  text-align: center;
  
  &.with-icon {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

export const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #1d334e, #BF1922);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  &.large {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    
    svg {
      width: 32px;
      height: 32px;
    }
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: #ffffff;
  }
`;

export const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 16px;
  line-height: 1.3;
  
  &.large {
    font-size: 24px;
    margin-bottom: 20px;
  }

  &.xlarge {
    font-size: 32px;
    margin-bottom: 24px;
  }
  
  &.small {
    font-size: 18px;
    margin-bottom: 12px;
  }
`;

export const CardDescription = styled.p`
  font-size: 15px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin: 0;
  
  &.large {
    font-size: 16px;
  }
  
  &.small {
    font-size: 14px;
  }
`;

// ============================================================================
// CONTENT LAYOUT COMPONENTS  
// ============================================================================
export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-top: 60px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

export const InfoTitle = styled.h3`
  font-size: 30px;
  font-weight: 800;
  color: #1d334e;
  margin-bottom: 12px;
`;

/** Section / two-column card headings on solutions landings (SectionPage, SubsectionPage) */
export const SolutionsSectionHeading = styled.h3`
  font-size: clamp(28px, 4vw, 30px);
  font-weight: 800;
  color: #1d334e;
  margin-bottom: 12px;
  line-height: 1.3;
`;

export const InfoText = styled.p`
  font-size: 16px;
  color: #1d334e;
  line-height: 1.6;
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// ============================================================================
// CONTENT SECTION COMPONENTS FOR SOLUTIONS PAGES
// ============================================================================
export const ContentSection = styled(Section)`
  margin-bottom: 0;
  
  &:last-child {
    padding-bottom: 0;
  }
`;

export const ImageTextSection = styled(TwoColumnGrid)`
  align-items: center;
  margin: 60px 0;
  
  @media (max-width: 968px) {
    margin: 40px 0;
  }
`;

export const ImageTextImage = styled(ImageContainer)`
  margin-bottom: 0;
  height: auto;
  min-height: 0;
  aspect-ratio: 9/5;
  width: 100%;
  
  @media (max-width: 968px) {
    order: -1; /* On mobile, always show image first to avoid two images adjacent when sections alternate left/right */
  }
`;

export const ImageTextContent = styled(ContentText)`
  margin: 0;
`;

// ============================================================================
// SIMPLE PAGE TEMPLATE
// ============================================================================
export const SimplePage = ({ title, description, children }) => {
  return (
    <PageContainer>
      <Container>
        <PageTitle className="with-underline" {...fadeInUp}>
          {title}
        </PageTitle>
        <PageSubtitle {...fadeInUp} style={{ transitionDelay: '0.1s' }}>
          {description}
        </PageSubtitle>
        {children}
      </Container>
    </PageContainer>
  );
};

// Template renderer for dynamic content with bold formatting
export const TemplatedText = ({ paragraph }) => {
  if (!paragraph) return null;

  const { text, boldStart, boldText } = paragraph;

  if (boldStart) {
    return (
      <p>
        <strong>{boldStart}</strong>{text}
      </p>
    );
  }

  if (boldText) {
    const parts = text.split('{values}').length > 1
      ? text.split('{values}')
      : text.split('{industries}').length > 1
        ? text.split('{industries}')
        : [text];

    if (parts.length > 1) {
      return (
        <p>
          {parts[0]}<strong>{boldText}</strong>{parts[1]}
        </p>
      );
    }
  }

  return <p>{text}</p>;
};

// ============================================================================
// FULL-WIDTH BLUR SECTION (shared base for Newsletter + Discuss CTA)
// ============================================================================
const FullWidthBlurSection = styled.section`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  padding: 80px 32px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

// ============================================================================
// NEWSLETTER COMPONENTS (Reusable across pages)
// ============================================================================
export const NewsletterSection = styled(FullWidthBlurSection)``;

export const NewsletterCard = styled(motion.div)`
  text-align: center;
  margin: 0 auto;
`;

export const NewsletterTitle = styled(motion.h3)`
  font-size: clamp(24px, 4vw, 30px);
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 16px;
  letter-spacing: -0.02em;
`;

export const NewsletterText = styled(motion.p)`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
`;

export const NewsletterForm = styled(motion.form)`
  display: flex;
  gap: 16px;
  max-width: 80%;
  margin: 0 auto 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const NewsletterInput = styled.input`
  flex: 1;
  padding: 16px 20px;
  border: 2px solid rgba(191, 25, 34, 0.3);
  border-radius: 50px;
  background: rgba(191, 25, 34, 0.01);
  backdrop-filter: blur(10px);
  font-size: 16px;
  color: #1d334e;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &::placeholder {
    color: rgba(29, 51, 78, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(191, 25, 34, 0.8);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0 0 4px rgba(191, 25, 34, 0.1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const NewsletterButton = styled.button`
  padding: 16px 32px;
  background: linear-gradient(135deg, #BF1922, #8B1419);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: clamp(14px, 1.8vw, 16px);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const StatusMessage = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  
  &.success {
    background: rgba(34, 197, 94, 0.1);
    color: #16a34a;
    border: 1px solid rgba(34, 197, 94, 0.2);
  }
  
  &.error {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
    border: 1px solid rgba(239, 68, 68, 0.2);
  }
`;

// Newsletter Hook for shared functionality
export const useNewsletter = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setStatus('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setStatus('');

    try {
      const response = await fetch('/api/newsletter.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If response is not valid JSON
        setStatus(response.ok
          ? 'Invalid response from server. Please try again.'
          : `Server error: ${response.status} ${response.statusText || 'Unknown error'}`);
        return;
      }

      if (data.success) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);

      // Clear status message after 5 seconds
      setTimeout(() => {
        setStatus('');
      }, 5000);
    }
  };

  return {
    email,
    setEmail,
    isSubmitting,
    status,
    handleSubmit
  };
};

// ============================================================================
// HERO SECTION COMPONENTS
// ============================================================================
export const HeroSection = styled.section`
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  /* Background comes from LazyBackgroundImage wrapper; this section is content-only */
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.4), rgba(0,0,0,0.6));
    z-index: 1;
  }
`;

/** Mobile hero tweaks for solutions + strategic-intelligence landings */
export const SolutionsHeroSection = styled(HeroSection)`
  /* Shorter than full-viewport hero; pair with LazyBackgroundImage ~50% 70% (60/40 vertical crop top/bottom) */
  min-height: clamp(600px, 42vh, 680px);

  @media (max-width: 768px) {
    padding-top: 70px;
    min-height: auto;
    align-items: flex-start;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  /* Reduced animation delay for Speed Index optimization - content appears faster */
  animation: ${fadeInUpKeyframe} 0.6s ease-out 0.1s both;
  text-align: center;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(48px, 10vw, 55px);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 24px;
  line-height: 0.9;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(18px, 2vw, 18px);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin: 0 auto 40px;
  line-height: 1.4;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

/** Wrapper for hero title + subtitle (padding, width). Used in SectionPage, SubsectionPage, StrategicIntelligence. */
export const HeroContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 52px 0;

  @media (max-width: 768px) {
    padding: 88px 0 36px 0;
  }
`;

export const CTAButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  padding: 16px 30px;
  border-radius: 50px;
  font-weight: 500;
  font-size: clamp(14px, 2vw, 18px);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.15);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

// ============================================================================
// BRIDGE CTA (image + content + button) — used on About and Services
// ============================================================================
export const BridgeSection = styled(motion.div)`
  margin: 32px auto 0;
  text-align: center;
  position: relative;
  padding: 60px 40px;
  border-radius: 24px;
  overflow: hidden;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 40px 20px;
    border-radius: 16px;
    min-height: 240px;
  }
`;

export const BridgeContent = styled.div`
  position: relative;
  z-index: 2;
  margin: 0 auto;
`;

export const BridgeTitle = styled(motion.h3)`
  font-size: clamp(24px, 4vw, 30px);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 24px;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  line-height: 1.3;
`;

export const BridgeButton = styled(LocaleLink)`
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  padding: 16px 30px;
  border-radius: 50px;
  font-weight: 500;
  font-size: clamp(14px, 2vw, 18px);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

export const StrategicIntelligenceBackLink = styled(LocaleLink)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 32px;
  font-size: 15px;
  font-weight: 500;
  color: #bf1922;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #8b1419;
  }

  svg {
    margin-right: 8px;
    width: 18px;
    height: 18px;
  }
`;

export function StrategicIntelligenceBackArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

// Subsections grid and link for section hero (used in SectionPage)
export const SubsectionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 48px;
  max-width: 1200px;
  
  @media (max-width: 768px) {
    gap: 10px;
    margin-top: 32px;
  }
`;

export const SubsectionLink = styled.a`
  text-decoration: none;
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  padding: 12px 24px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  width: fit-content;
  min-width: 200px;
  
  &:hover {
    color: rgba(255, 255, 255, 1);
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateX(4px);
  }
  
  &:active {
    transform: translateX(2px);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 10px 20px;
    min-width: auto;
    width: 100%;
    
    &:hover {
      transform: translateX(2px);
    }
  }
`;

// ============================================================================
// FEATURE BANNER COMPONENTS (extracted from Home.js)
// ============================================================================
export const FeatureBanner = styled(motion.div)`
  border-radius: 24px;
  overflow: hidden;
  height: clamp(260px, 36vw, 320px);
  max-width: 1100px;
  background-size: cover;
  background-position: center;
  position: relative;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    height: clamp(220px, 60vw, 280px);
    margin: 0 auto;
  }
`;

export const FeatureBannerOverlay = styled.div`
  position: absolute;
  left: 32px;
  bottom: 32px;
  width: min(420px, calc(100% - 64px));
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(18px);
  border-radius: 18px;
  padding: 20px 24px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    left: 18px;
    right: 18px;
    bottom: 20px;
    width: calc(100% - 36px);
    padding: 18px 20px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    width: calc(100% - 32px);
    left: 16px;
    right: 16px;
    padding: 16px 18px;
    bottom: 16px;
  }
`;

export const FeatureBannerTitle = styled.h3`
  font-size: clamp(20px, 4vw, 28px);
  font-weight: 700;
  color: #1d334e;
  margin: 0;
`;

export const FeatureBannerText = styled.p`
  font-size: 15px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.5;
  margin: 0;
`;

// ============================================================================
// INDUSTRY / SOLUTION CARD (Sector Expertise, Home & Services)
// ============================================================================
export const IndustryCardLink = styled.a`
  position: relative;
  border-radius: 24px;
  background-color: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  display: grid;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? 'minmax(0, 1.1fr) minmax(0, 1.4fr)' : '1fr')};
  min-height: 180px;
  align-items: stretch;
  text-decoration: none;
  color: inherit;

  &:hover {
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const IndustryCardImage = styled.div`
  display: ${({ $hasImage }) => ($hasImage ? 'block' : 'none')};
  background: ${({ $image }) => $image ? `url(${$image}) center/cover` : 'none'};
  min-height: 0;
  height: 100%;
  align-self: stretch;

  @media (max-width: 768px) {
    aspect-ratio: 16/10;
    height: 100%;
    align-self: auto;
  }
`;

export const IndustryCardContent = styled.div`
  padding: clamp(15px, 4vw, 20px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
`;

export const IndustryCardTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 8px;
  line-height: 1.3;
`;

export const IndustryCardDescription = styled.p`
  font-size: 14px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin: 0;
`;

/** Arrow icon for clickable cards (Sector Expertise, SectionPage) */
export const CardArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/** Arrow button badge for clickable cards - positioned top-right */
export const CardArrowButton = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  background: linear-gradient(135deg, #BF1922 0%, #8B1419 100%);
  color: #ffffff;
  border-radius: 0 16px 0 16px;
  transition: filter 0.25s ease;

  svg {
    display: block;
  }

  a:hover &,
  a:focus & {
    filter: brightness(1.08);
  }
`;

// ============================================================================
// PARTNERSHIP CTA SECTION (shared: Home, SectionPage, SubsectionPage)
// Use inside LazyBackgroundImage with overlay prop. Content only — no image/overlay.
// ============================================================================
export const PartnershipSection = styled(motion.div)`
  margin: 0 auto;
  text-align: center;
  position: relative;
  padding: 60px 40px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 40px 20px;
    min-height: 180px;
  }
`;

export const PartnershipContent = styled.div`
  position: relative;
  z-index: 2;
  margin: 0 auto;
`;

export const PartnershipTitle = styled(motion.h3)`
  font-size: clamp(24px, 4vw, 30px);
  font-weight: 900;
  color: #ffffff;
  margin-bottom: 16px;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
`;

export const PartnershipText = styled(motion.p)`
  font-size: clamp(14px, 2.5vw, 16px);
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  margin-bottom: 24px;
`;

export const PartnershipButton = styled.a`
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  padding: 16px 30px;
  border-radius: 50px;
  font-weight: 500;
  font-size: clamp(14px, 2vw, 18px);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

/** SectionPage / SubsectionPage image CTAs — compact title & body; button keeps Partnership glass look, Services-sized type & padding */
export const SolutionsCtaTitle = styled(PartnershipTitle)`
  font-size: clamp(20px, 3.2vw, 24px);
  margin-bottom: 12px;
`;

export const SolutionsCtaText = styled(PartnershipText)`
  font-size: clamp(13px, 2vw, 15px);
  line-height: 1.55;
  margin-bottom: 20px;
`;

export const SolutionsCtaButton = styled(PartnershipButton).attrs({ as: LocaleLink })`
  padding: 18px 48px;
  font-size: 16px;
`;

/** Primary red gradient CTA — same styling as `/services` bottom CTA */
export const ContactButton = styled(LocaleLink)`
  display: inline-block;
  padding: 18px 48px;
  background: linear-gradient(135deg, #bf1922, #8b1419);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 50px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 4px 20px rgba(191, 25, 34, 0.3);

  &:hover {
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(191, 25, 34, 0.4);
  }
`;

// ============================================================================
// SEPARATOR IMAGE COMPONENT (extracted from Services.js)
// ============================================================================
export const SeparatorImage = styled(motion.div)`
  margin: 60px 0;
  border-radius: 24px;
  overflow: hidden;
  height: 300px;
  background-size: cover;
  background-position: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    height: 200px;
    margin: 40px 0;
  }
`;

export const SectionDivider = styled.hr`
  border: none;
  width: 80%;
  height: 1px;
  margin: 0px auto 16px auto;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(0, 0, 0, 0.1) 50%,
    transparent 100%
  );
`;

// ============================================================================
// STYLED BULLET LIST COMPONENTS (clean vertical list with icons)
// ============================================================================

// Container with optional blurred background image
export const BulletCardsContainer = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  padding: 48px;
  margin: 50px 0;
  
  ${props => props.$backgroundImage ? `
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: url("${props.$backgroundImage}") center/cover no-repeat;
      filter: blur(6px) brightness(0.6);
      z-index: 0;
      transform: scale(1.1);
    }
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(0,0,0,0.2), rgba(0,0,0,0.4));
      z-index: 1;
    }
  ` : `
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  `}
  
  @media (max-width: 768px) {
    padding: 32px 24px;
    margin: 40px 0;
  }
`;

export const BulletCardsContent = styled.div`
  position: relative;
  z-index: 2;
`;

export const BulletCardsTitle = styled.h3`
  font-size: clamp(24px, 4vw, 32px);
  font-weight: 700;
  color: ${props => props.$hasBackground ? '#ffffff' : '#1d334e'};
  margin-bottom: 28px;
  line-height: 1.3;
  ${props => props.$hasBackground && 'text-shadow: 0 2px 10px rgba(0,0,0,0.3);'}
`;

// Simple vertical list - NOT a grid
export const BulletCardsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Individual bullet item - clean horizontal layout
export const BulletCard = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  background: ${props => props.$hasBackground ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)'};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 640px) {
    padding: 16px 20px;
    gap: 14px;
  }
`;

export const BulletCardIcon = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: linear-gradient(135deg, #1d334e, #BF1922);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  svg {
    width: 18px;
    height: 18px;
    color: white;
  }
`;

export const BulletCardText = styled.p`
  font-size: 16px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.65;
  margin: 0;
  flex: 1;
  
  strong {
    color: #1d334e;
    font-weight: 600;
  }
`;

// Strategic Intelligence articles & About — red left-accent item cards
export const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0 24px;
`;

/** 2-column grid of article-style cards (About page, wide article sections) */
export const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin: 24px 0;
  text-align: left;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ItemCard = styled.div`
  padding: 24px 28px;
  background: rgba(29, 51, 78, 0.03);
  border-radius: 12px;
  border-left: 3px solid #bf1922;
`;

/**
 * Same red left accent as ItemCard; background matches Global Operating Footprint stat cards (GlobalStats StatCard).
 */
export const ItemCardLight = styled(ItemCard)`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(15px);
  border-left: 3px solid #bf1922;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  }
`;

/** Numbered badge cards — Home “Execution Platform Capabilities”, About “Our Operating Architecture” */
export const ExecutionCapabilityCard = styled(ItemCardLight)`
  position: relative;
  overflow: visible;
  padding: 28px 28px 24px 38px;
  opacity: ${(p) => (p.$isVisible !== false ? 1 : 0)};
  transform: translateY(${(p) => (p.$isVisible !== false ? '0' : '30px')});
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(p) => p.$delay || '0s'};
`;

export const ExecutionCapabilityNumber = styled.span`
  position: absolute;
  z-index: 2;
  top: -10px;
  left: -10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #bf1922;
  border: none;
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(191, 25, 34, 0.35);
  line-height: 1;
  user-select: none;
`;

/** Solution subsection product pages — same visual as ItemCard, motion-compatible via motion(SolutionBulletCard) */
export const SolutionBulletCard = styled(ItemCard)`
  text-align: left;
`;

/** Light variant for solution pages — pairs with ItemCardLight */
export const SolutionBulletCardLight = styled(ItemCardLight)`
  text-align: left;
`;

export const ItemTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 10px;
`;

export const ItemNumber = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: #bf1922;
  flex-shrink: 0;
`;

export const ItemTitle = styled.h4`
  font-size: 17px;
  font-weight: 700;
  color: #1d334e;
  margin: 0;
  line-height: 1.3;
`;

export const ItemBody = styled.div`
  font-size: 15px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;

  p {
    margin: 0 0 12px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Checkmark icon for bullet cards
export const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ============================================================================
// LAYOUT PATTERN UTILITY (for randomized section layouts)
// ============================================================================

// Generate a deterministic hash from a string
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Get layout pattern for image-text sections based on subsection ID
// Returns an array of layout types: 'image-side', 'blurred-bg', 'text-only'
export const getLayoutPattern = (subsectionId, sectionCount) => {
  const hash = hashString(subsectionId || 'default');
  const patterns = [];

  // Determine how many sections get each style based on hash
  const imageSideCount = 2 + (hash % 2); // 2-3 sections with side-by-side images
  const blurredBgCount = Math.min(3, Math.floor(sectionCount / 3)); // Up to 3 with blurred backgrounds

  // Create pattern based on hash
  const startOffset = hash % 3; // Where to start placing image-side layouts

  for (let i = 0; i < sectionCount; i++) {
    const adjustedIndex = (i + startOffset) % sectionCount;

    if (adjustedIndex < imageSideCount) {
      patterns[i] = 'image-side';
    } else if (adjustedIndex < imageSideCount + blurredBgCount) {
      patterns[i] = 'blurred-bg';
    } else {
      patterns[i] = 'text-only';
    }
  }

  // Shuffle based on hash for more variety
  const shuffleSeed = hash % 1000;
  for (let i = patterns.length - 1; i > 0; i--) {
    const j = (shuffleSeed + i) % (i + 1);
    [patterns[i], patterns[j]] = [patterns[j], patterns[i]];
  }

  return patterns;
};
